import { AlertTriangle, ArrowLeft, ArrowRight, FileSearch, HelpCircle, RefreshCw, RotateCcw } from 'lucide-react'

import { Button } from '@krp/ui-kit'

import '../../../shared/uc1b/journals.css'
import '../../../shared/ui/aqsi.css'
import { QueryState } from '@krp/sale-ui'
import { StateBadge } from '@krp/sale-ui'
import '@krp/sale-ui/sale.css'
import type { IncidentsActions, IncidentsVm } from '../model/types'

/**
 * Журнал инцидентов места (C4b): все незавершённые продажи и возвраты, неопределённые —
 * первыми. Только чтение: отсюда не уходит ни одна команда кассе; разбор — в окне
 * продажи/возврата, доказательство восстановления — своим окном.
 */
export function IncidentsScreen({ vm, actions }: { vm: IncidentsVm; actions: IncidentsActions }) {
  return (
    <section className="aqsi-journal" aria-labelledby="aqsi-incidents-title">
      <header className="aqsi-journal__head">
        <h2 id="aqsi-incidents-title">Инциденты продаж и возвратов</h2>
        <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload}>
          Обновить
        </Button>
      </header>
      <QueryState query={vm.registersQuery} what="Кассы" emptyText="Касс нет" onRetry={actions.reload} />
      {vm.registersQuery.status === 'ready' && vm.seat === null && (
        <section className="aqsi-panel" data-tone="warning" aria-label="Место не выбрано">
          <h3>
            <AlertTriangle size={16} aria-hidden="true" /> Вы не работаете за кассой этого кабинета
          </h3>
          <p className="aqsi-card__text">Журнал места читает тот, кто сейчас работает за его кассой.</p>
          <div className="aqsi-card__actions">
            <Button onClick={actions.openRmk}>Открыть РМК</Button>
          </div>
        </section>
      )}
      {vm.query !== null && vm.cursorProblem && (
        <section className="aqsi-panel" data-tone="warning" role="alert">
          <h3>
            <AlertTriangle size={16} aria-hidden="true" /> Выдачу нужно начать заново
          </h3>
          <p className="aqsi-card__text">{vm.query.error?.message}</p>
          <div className="aqsi-card__actions">
            <Button variant="primary" icon={<RotateCcw size={14} aria-hidden="true" />} onClick={actions.restart}>
              Начать заново
            </Button>
          </div>
        </section>
      )}
      {vm.query !== null && !vm.cursorProblem && (
        <QueryState
          query={vm.query}
          what="Журнал"
          emptyText="Незавершённых продаж и возвратов нет"
          onRetry={actions.reload}
        />
      )}
      {vm.rows.length > 0 && (
        <ul className="aqsi-journal__list" aria-label="Инциденты">
          {vm.rows.map((row) => (
            <li key={row.saleId} className="aqsi-journal__item" data-attention={row.attention}>
              <div className="aqsi-journal__item-main">
                {row.attention ? <HelpCircle size={16} aria-hidden="true" /> : <AlertTriangle size={16} aria-hidden="true" />}
                <strong>{row.attentionText}</strong>
                <span>{row.createdText}</span>
              </div>
              <StateBadge badge={row.money} />
              <StateBadge badge={row.receipt} />
              <div className="aqsi-journal__meta">
                <span>Продажа {row.saleId}</span>
                {row.notes.map((text) => (
                  <span key={text}>{text}</span>
                ))}
              </div>
              <div className="aqsi-card__actions">
                <Button
                  variant="ghost"
                  icon={<FileSearch size={14} aria-hidden="true" />}
                  onClick={() => actions.openProof(row.saleId)}
                  aria-label={`Доказательство восстановления продажи ${row.saleId}`}
                >
                  Доказательство
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {vm.query?.status === 'ready' && (
        <nav className="aqsi-journal__pager" aria-label="Страницы журнала">
          <Button icon={<ArrowLeft size={16} aria-hidden="true" />} disabled={!vm.canPrev} onClick={actions.prev}>
            Назад
          </Button>
          <span aria-live="polite">{vm.pageText}</span>
          <Button icon={<ArrowRight size={16} aria-hidden="true" />} disabled={!vm.canNext} onClick={actions.next}>
            Дальше
          </Button>
        </nav>
      )}
    </section>
  )
}
